import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { reverseGeocode } from './traccar';

// native app = dark glass skin
const IS_NATIVE = typeof window !== 'undefined' && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
// Neutral charcoal (not navy) so the sheet doesn't read as "all blue".
const PANEL = IS_NATIVE ? '#17181d' : 'white';
const TXT = IS_NATIVE ? '#f1f1f4' : '#0f172a';
const TXT_MUTED = IS_NATIVE ? '#a1a1aa' : '#64748b';
const HAIR = IS_NATIVE ? '1px solid rgba(255,255,255,0.08)' : '1px solid #eef2f7';

// Alert-detail screen: a map pinned at exactly where a warning fired, plus the
// details (device, date, time, lat/lng, speed, address). Used by the alert
// banner and the Alerts history list. `alert` = { icon, title, entity, place }.
export default function AlertDetailModal({ alert, onClose }) {
  const p = (alert && alert.place) || {};
  const v = (alert && alert.vitals) || null;
  const [addr, setAddr] = useState(p.address || '');
  const [copied, setCopied] = useState(false);
  const mapRef = useRef(null);

  // Lat/lng shown together so it can be copied and pasted anywhere in one go
  // (Google Maps, a message, a form). Tapping the copy button puts
  // "lat, lng" on the clipboard.
  const coordStr = p.lat != null ? `${p.lat.toFixed(6)}, ${p.lng.toFixed(6)}` : '';
  const copyCoords = async () => {
    if (!coordStr) return;
    try { await navigator.clipboard.writeText(coordStr); }
    catch {
      const ta = document.createElement('textarea'); ta.value = coordStr;
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  // Tap the map → open the spot in Google Maps; the Street View button drops
  // the customer into the panorama there.
  const mapsUrl = p.lat != null ? `https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}` : '';
  const streetViewUrl = p.lat != null ? `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${p.lat},${p.lng}` : '';

  // Clean roadmap (NOT satellite) pinned at the alert. Uses CartoDB Voyager
  // tiles — a crisp Google-style road map that needs no API key, so it can't
  // break on a key/referrer restriction the way the Google Static Maps image
  // did.
  useEffect(() => {
    if (!mapRef.current || p.lat == null) return undefined;
    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false, dragging: true, scrollWheelZoom: false }).setView([p.lat, p.lng], 16);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', { maxZoom: 20 }).addTo(map);
    const icon = L.divIcon({ className: '', iconSize: [30, 30], iconAnchor: [15, 28], html: '<div style="font-size:26px;line-height:1;filter:drop-shadow(0 2px 3px rgba(0,0,0,.5))">📍</div>' });
    L.marker([p.lat, p.lng], { icon }).addTo(map);
    setTimeout(() => map.invalidateSize(), 120);
    if (!addr) reverseGeocode(p.lat, p.lng).then((r) => { if (r && (r.display || r.road)) setAddr(r.display || r.road); }).catch(() => {});
    return () => map.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.lat, p.lng]);

  const when = p.at ? new Date(p.at) : null;
  const Row = ({ k, v }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '11px 4px', borderBottom: HAIR, fontSize: '13px' }}>
      <span style={{ color: TXT_MUTED, fontWeight: 600 }}>{k}</span>
      <span style={{ color: TXT, fontWeight: 700, textAlign: 'right' }}>{v}</span>
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 6000, background: 'rgba(3,4,7,0.74)', WebkitBackdropFilter: 'blur(4px)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', background: PANEL, borderRadius: '22px', width: 'min(460px, 96vw)', maxHeight: '92vh', overflowY: 'auto', border: IS_NATIVE ? '1px solid rgba(255,255,255,0.14)' : '1px solid #e5e7eb', boxShadow: '0 30px 90px rgba(0,0,0,0.65), 0 2px 10px rgba(0,0,0,0.4)' }}>
        {/* grab handle so it clearly reads as a sheet sitting on top of the app */}
        <div style={{ position: 'absolute', top: '8px', left: '50%', transform: 'translateX(-50%)', width: '40px', height: '4px', borderRadius: '999px', background: 'rgba(255,255,255,0.55)', zIndex: 3, pointerEvents: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 16px 14px', background: 'linear-gradient(135deg,#dc2626,#991b1b)', color: 'white', borderTopLeftRadius: '21px', borderTopRightRadius: '21px' }}>
          <span style={{ fontSize: '20px' }}>{alert.icon}</span>
          <div style={{ flex: 1, fontWeight: 800, fontSize: '15px' }}>{alert.title}</div>
          <button onClick={onClose} aria-label="Close" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, background: 'rgba(255,255,255,0.22)', color: 'white', border: 'none', borderRadius: '50%', width: '28px', height: '28px', padding: 0, cursor: 'pointer', fontSize: '17px', flexShrink: 0 }}>×</button>
        </div>
        {p.lat != null
          ? <div ref={mapRef} style={{ height: '240px', width: '100%', background: IS_NATIVE ? '#0a0f1a' : '#e2e8f0' }} />
          : (
            <div style={{ padding: '22px 20px', textAlign: 'center', color: TXT_MUTED, fontSize: '13px', lineHeight: 1.6 }}>
              <div style={{ fontSize: '30px', marginBottom: '6px' }}>📍</div>
              No GPS position was recorded for this alert.
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Alerts like a tracker going offline have no location, because the device had already stopped reporting.
              </div>
            </div>
          )}
        <div style={{ padding: '14px 16px' }}>
          <div style={{ fontWeight: 800, fontSize: '14px', textAlign: 'center', color: TXT, marginBottom: '10px' }}>Alert detail</div>
          <div style={{ background: IS_NATIVE ? 'rgba(239,68,68,0.14)' : '#fef2f2', border: `1px solid ${IS_NATIVE ? 'rgba(239,68,68,0.32)' : '#fecaca'}`, borderRadius: '12px', padding: '11px 13px', color: IS_NATIVE ? '#fca5a5' : '#b91c1c', fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>
            {p.device || alert.entity} generated <b>{String(alert.title || '').toUpperCase()}</b>{when ? ` on ${when.toLocaleString()}` : ''}
          </div>
          {alert.lastKnown && (
            <div style={{ background: IS_NATIVE ? 'rgba(251,191,36,0.14)' : '#fffbeb', border: `1px solid ${IS_NATIVE ? 'rgba(251,191,36,0.3)' : '#fde68a'}`, borderRadius: '10px', padding: '9px 11px', color: IS_NATIVE ? '#fcd34d' : '#b45309', fontSize: '12px', marginBottom: '10px' }}>
              ⏱️ This alert had no GPS fix of its own — the map shows the car's <b>last known position</b>.
            </div>
          )}
          <Row k="Vehicle" v={p.device || alert.entity || '—'} />
          <Row k="Device" v={p.imei || '—'} />
          <Row k="Alert" v={alert.title || '—'} />
          <Row k="Date" v={when ? when.toLocaleDateString() : '—'} />
          <Row k="Hour" v={when ? when.toLocaleTimeString() : '—'} />
          {/* One copyable line so it can be pasted anywhere at once */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '11px 4px', borderBottom: HAIR, fontSize: '13px', alignItems: 'center' }}>
            <span style={{ color: TXT_MUTED, fontWeight: 600 }}>Coordinates</span>
            {p.lat != null ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <span onClick={copyCoords} title="Tap to copy" style={{ color: TXT, fontWeight: 700, fontFamily: 'monospace', userSelect: 'all', cursor: 'pointer', whiteSpace: 'nowrap' }}>{coordStr}</span>
                <button onClick={copyCoords} title="Copy coordinates" style={{ flexShrink: 0, background: copied ? '#16a34a' : (IS_NATIVE ? 'rgba(255,255,255,0.1)' : '#eef2f7'), color: copied ? 'white' : TXT, border: 'none', borderRadius: '7px', padding: '4px 9px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>{copied ? '✓ Copied' : '📋 Copy'}</button>
              </span>
            ) : <span style={{ color: TXT, fontWeight: 700 }}>—</span>}
          </div>
          <Row k="Speed" v={p.speed != null ? `${p.speed} mph` : '—'} />
          <Row k="Address" v={p.lat != null ? (addr || 'Looking up…') : '—'} />
          {alert.info && <Row k="Details" v={alert.info} />}
          {v && Object.keys(v).some((k) => v[k] != null && v[k] !== '') && (
            <>
              <div style={{ fontWeight: 800, fontSize: '13px', color: TXT, margin: '16px 0 8px' }}>
                Readings when this fired
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {[
                  ['Speed', v.speed != null ? `${v.speed} mph` : null],
                  ['Ignition', v.ignition == null ? null : (v.ignition ? 'On' : 'Off')],
                  ['Fuel', v.fuel != null ? `${Math.round(v.fuel)}%` : null],
                  ['Coolant', v.coolant != null ? `${Math.round(v.coolant)}°C` : null],
                  ['RPM', v.rpm != null ? Math.round(v.rpm) : null],
                  ['Battery', v.battery != null ? `${Number(v.battery).toFixed(1)}V` : null],
                  ['Fault codes', v.faults != null ? v.faults : null],
                  ['Odometer', v.odometer != null ? `${Math.round(v.odometer / 1609)} mi` : null],
                  ['Satellites', v.sats != null ? v.sats : null],
                ].filter((r) => r[1] != null).map(([k, val]) => (
                  <div key={k} style={{ background: IS_NATIVE ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: HAIR, borderRadius: '10px', padding: '9px 11px' }}>
                    <div style={{ fontSize: '10px', color: TXT_MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{k}</div>
                    <div style={{ fontSize: '15px', color: TXT, fontWeight: 800, marginTop: '2px' }}>{val}</div>
                  </div>
                ))}
              </div>
              {v.codes ? (
                <div style={{ marginTop: '8px', background: IS_NATIVE ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: HAIR, borderRadius: '10px', padding: '9px 11px' }}>
                  <div style={{ fontSize: '10px', color: TXT_MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>Codes</div>
                  <div style={{ fontSize: '14px', color: TXT, fontWeight: 800, marginTop: '2px' }}>{v.codes}</div>
                </div>
              ) : null}
              <div style={{ fontSize: '11px', color: TXT_MUTED, marginTop: '8px', lineHeight: 1.5 }}>
                These are saved as-of the alert — they don't change as the car keeps driving.
              </div>
            </>
          )}
          {p.lat != null && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <a
                href={mapsUrl} target="_blank" rel="noreferrer"
                style={{ flex: 1, textAlign: 'center', textDecoration: 'none', background: 'linear-gradient(135deg,#f8703c,#f4511e)', color: 'white', fontWeight: 800, fontSize: '14px', borderRadius: '12px', padding: '12px' }}
              >🧭 Open in Maps</a>
              <a
                href={streetViewUrl} target="_blank" rel="noreferrer"
                style={{ flex: 1, textAlign: 'center', textDecoration: 'none', background: IS_NATIVE ? 'rgba(255,255,255,0.08)' : '#f1f5f9', color: TXT, border: HAIR, fontWeight: 800, fontSize: '14px', borderRadius: '12px', padding: '12px' }}
              >👁️ Street View</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
